/**
 * Development-only logger utility
 * Logs are only output in development environment
 * Supports styled formatting with timestamps and log levels
 * Works in both Node.js and browser environments
 */

type LogLevel = "debug" | "info" | "warn" | "error";

interface LogConfig {
  label: string;
  nodeColor: string;
  browserColor: string;
  emoji: string;
}

const LOG_CONFIG: Record<LogLevel, LogConfig> = {
  debug: {
    label: "DEBUG",
    nodeColor: "\x1b[90m", // gray
    browserColor: "color: #6b7280; font-weight: bold;",
    emoji: "🔍",
  },
  info: {
    label: "INFO",
    nodeColor: "\x1b[36m", // cyan
    browserColor: "color: #06b6d4; font-weight: bold;",
    emoji: "ℹ️",
  },
  warn: {
    label: "WARN",
    nodeColor: "\x1b[33m", // yellow
    browserColor: "color: #f59e0b; font-weight: bold;",
    emoji: "⚠️",
  },
  error: {
    label: "ERROR",
    nodeColor: "\x1b[31m", // red
    browserColor: "color: #ef4444; font-weight: bold;",
    emoji: "❌",
  },
};

const RESET = "\x1b[0m";

const isDev = process.env.NODE_ENV !== "production";

/**
 * Check if running in browser environment
 */
function isBrowser(): boolean {
  return typeof window !== "undefined" && typeof window.console !== "undefined";
}

/**
 * Get current timestamp in HH:MM:SS format
 */
function getTimestamp(): string {
  const now = new Date();
  return now.toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  });
}

/**
 * Log for Node.js environment using ANSI colors
 */
function logNode(
  level: LogLevel,
  message: string,
  data?: unknown,
  label?: string
): void {
  const config = LOG_CONFIG[level];
  const timestamp = `${RESET}\x1b[90m[${getTimestamp()}]${RESET}`;
  const levelStr = `${config.nodeColor}[${config.label}]${RESET}`;
  const labelStr = label ? ` ${RESET}\x1b[35m[${label}]${RESET}` : "";

  const prefix = `${timestamp} ${levelStr}${labelStr}`;

  if (data !== undefined) {
    console.log(`${prefix} ${message}`, data);
  } else {
    console.log(`${prefix} ${message}`);
  }
}

/**
 * Log for browser environment using CSS styling
 */
function logBrowser(
  level: LogLevel,
  message: string,
  data?: unknown,
  label?: string
): void {
  const config = LOG_CONFIG[level];
  const timestamp = `[${getTimestamp()}]`;
  const levelLabel = `[${config.label}]`;
  const labelStr = label ? ` [${label}]` : "";

  // Base styles
  const timestampStyle = "color: #9ca3af; font-weight: normal;";
  const labelStyle = "color: #a855f7; font-weight: bold;"; // purple for custom labels
  const messageStyle = "color: inherit; font-weight: normal;";

  // Build the format string and styles array
  const parts: string[] = [];
  const styles: string[] = [];

  // Timestamp
  parts.push(`%c${timestamp}`);
  styles.push(timestampStyle);

  // Level with its color
  parts.push(` %c${levelLabel}`);
  styles.push(config.browserColor);

  // Custom label if provided
  if (label) {
    parts.push(`%c${labelStr}`);
    styles.push(labelStyle);
  }

  // Message
  parts.push(` %c${message}`);
  styles.push(messageStyle);

  // Combine
  const formatString = parts.join("");

  if (data !== undefined) {
    console.log(formatString, ...styles, data);
  } else {
    console.log(formatString, ...styles);
  }
}

/**
 * Main log function - automatically detects environment
 */
function log(
  level: LogLevel,
  message: string,
  data?: unknown,
  label?: string
): void {
  if (!isDev) return;

  if (isBrowser()) {
    logBrowser(level, message, data, label);
  } else {
    logNode(level, message, data, label);
  }
}

/**
 * Logger utility with methods for different log levels
 */
export const logger = {
  /**
   * Debug level logging
   * Use for detailed debugging information
   */
  debug: (message: string, data?: unknown, label?: string) =>
    log("debug", message, data, label),

  /**
   * Info level logging
   * Use for general information
   */
  info: (message: string, data?: unknown, label?: string) =>
    log("info", message, data, label),

  /**
   * Warning level logging
   * Use for potential issues
   */
  warn: (message: string, data?: unknown, label?: string) =>
    log("warn", message, data, label),

  /**
   * Error level logging
   * Use for errors
   */
  error: (message: string, data?: unknown, label?: string) =>
    log("error", message, data, label),

  /**
   * Group related logs together
   * Only works in development
   */
  group: (label: string, fn: () => void) => {
    if (!isDev) return;
    console.group(`🔷 ${label}`);
    fn();
    console.groupEnd();
  },

  /**
   * Log a table (for arrays/objects)
   * Only works in development
   */
  table: (data: unknown[], label?: string) => {
    if (!isDev) return;
    if (label) {
      if (isBrowser()) {
        console.log(`%c[${label}]`, "color: #a855f7; font-weight: bold;");
      } else {
        console.log(`\x1b[35m[${label}]\x1b[0m`);
      }
    }
    console.table(data);
  },
};

export default logger;
